import type { QueryDefinition } from '@reduxjs/toolkit/dist/query';
import type { QueryArgFrom } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import type { QueryHooks } from '@reduxjs/toolkit/dist/query/react/buildHooks';

/**
 * This hook is a wrapper around the RTK Query useQuery hook that returns data if already loaded
 * even if the skip flag is false.
 *
 * This hook solves this scenario:
 * Data should only load on client if in view but data was already loaded on server. If the component
 * is not in view, the isLoadingTriggerInView flag is false and useQuery will not return any data
 * resulting in a mismatch between HTML rendered on server and client.
 *
 * @param config Defines the endpoint to use and the query params to pass to it
 * @returns
 */
export const useRTKQuery = <
    // Difficult to get the types right here, so we just use any for now
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    D extends QueryDefinition<any, any, any, any>,
    Q extends QueryArgFrom<D>
>({
    endpoint,
    queryParams,
    skip,
    isLoadingTriggerInView
}: {
    endpoint: QueryHooks<D>;
    queryParams: Q;
    skip: boolean;
    isLoadingTriggerInView?: boolean;
}) => {
    const { currentData, error } = endpoint.useQueryState(queryParams);
    const { isLoading } = endpoint.useQuery(queryParams, {
        // Skip if we already have data or trigger out of view
        skip:
            skip ||
            !!currentData ||
            (isLoadingTriggerInView !== undefined && !isLoadingTriggerInView)
    });

    return { data: currentData, isLoading: !currentData && isLoading, error };
};
